import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { SlidersHorizontal, Briefcase, MapPin, Plus, Clock } from "lucide-react";
import { api, fileUrl } from "../lib/api";
import { Pagination } from "../components/Pagination";
import { useTerms } from "../lib/useTerms";

const STATES = ["", "Florida", "Alabama", "Mississippi", "Louisiana", "Texas"];

export function JobCard({ j }) {
  const deadline = j.application_deadline ? new Date(j.application_deadline + "T23:59:59") : null;
  const daysLeft = deadline ? Math.ceil((deadline - new Date()) / 86400000) : null;
  return (
    <Link to={`/hiring/${j.slug}`} className="group block rounded-2xl bg-[#111] border border-white/10 card-hover p-5" data-testid={`job-card-${j.slug}`}>
      <div className="flex items-center justify-between mb-3">
        <span className="overline">{j.job_category || "Hospitality"}</span>
        <div className="flex items-center gap-2">
          {j.featured && <span className="text-xs bg-rose-500 rounded-full px-2.5 py-0.5 font-bold">Featured</span>}
          {daysLeft != null && daysLeft >= 0 && <span className="text-xs glass rounded-full px-2.5 py-1">{daysLeft === 0 ? "Closes today" : `${daysLeft}d to apply`}</span>}
        </div>
      </div>
      <h3 className="font-display font-bold text-lg leading-tight group-hover:text-rose-400 transition-colors">{j.title}</h3>
      <p className="text-sm text-zinc-400 mt-1.5">{j.business_name || j.venue_name}</p>
      <p className="text-sm text-zinc-500 flex items-center gap-1 mt-1"><MapPin size={13} /> {[j.city, j.state].filter(Boolean).join(", ") || "Gulf Coast"}</p>
      <div className="flex items-center justify-between mt-4 pt-4 border-t border-white/5 text-sm">
        <span className="text-zinc-300 flex items-center gap-1"><Clock size={13} /> {j.job_type || "—"}</span>
        {j.pay_range && <span className="font-display font-bold text-rose-400">{j.pay_range}{j.pay_type ? ` · ${j.pay_type}` : ""}</span>}
      </div>
    </Link>
  );
}

export default function Hiring() {
  const [data, setData] = useState({ items: [], total: 0, pages: 1 });
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [f, setF] = useState({ q: "", state: "", city: "", job_category: "", job_type: "", pay_type: "", sort: "newest", featured: false });
  const categories = useTerms("hiring", "job_category", []);
  const jobTypes = useTerms("hiring", "job_type", []);
  const payTypes = useTerms("hiring", "pay_type", []);

  useEffect(() => {
    const t = setTimeout(async () => {
      setLoading(true);
      const p = new URLSearchParams({ page, limit: 12, sort: f.sort });
      ["q", "state", "city", "job_category", "job_type", "pay_type"].forEach((k) => { if (f[k]) p.set(k, f[k]); });
      if (f.featured) p.set("featured", "true");
      try { const { data } = await api.get(`/hiring/jobs?${p.toString()}`); setData(data); }
      catch (e) { console.error("Failed to load hiring jobs:", e); }
      setLoading(false);
    }, 250);
    return () => clearTimeout(t);
  }, [f, page]);

  const set = (k, v) => { setF((s) => ({ ...s, [k]: v })); setPage(1); };
  const sel = "w-full bg-[#0a0a0a] border border-white/10 rounded-lg px-3 py-2.5 text-sm outline-none focus:border-rose-500";

  return (
    <div className="px-6 md:px-12 lg:px-20 py-10" data-testid="hiring-page">
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-8">
        <div>
          <p className="overline mb-2">Hospitality & entertainment jobs</p>
          <h1 className="font-display font-extrabold text-4xl md:text-5xl">Hiring Hub</h1>
          <p className="text-zinc-400 mt-2">Venues & businesses post openings. Apply with your resume in minutes.</p>
        </div>
        <Link to="/submit-job" className="bg-rose-500 hover:bg-rose-600 font-bold px-6 py-3 rounded-full flex items-center gap-2 w-fit" data-testid="post-job-btn"><Plus size={18} /> Post a Job</Link>
      </div>
      <div className="grid lg:grid-cols-12 gap-8">
        <aside className="lg:col-span-3">
          <div className="lg:sticky lg:top-20 bg-[#111] border border-white/10 rounded-xl p-5 space-y-4">
            <div className="flex items-center gap-2 font-semibold"><SlidersHorizontal size={16} /> Filters</div>
            <input value={f.q} onChange={(e) => set("q", e.target.value)} placeholder="Search jobs" className={sel} data-testid="hfilter-q" />
            <select value={f.state} onChange={(e) => set("state", e.target.value)} className={sel} data-testid="hfilter-state">{STATES.map((s) => <option key={s} value={s}>{s || "All States"}</option>)}</select>
            <input value={f.city} onChange={(e) => set("city", e.target.value)} placeholder="City" className={sel} data-testid="hfilter-city" />
            <select value={f.job_category} onChange={(e) => set("job_category", e.target.value)} className={sel} data-testid="hfilter-category"><option value="">All Categories</option>{categories.map((c) => <option key={c} value={c}>{c}</option>)}</select>
            <select value={f.job_type} onChange={(e) => set("job_type", e.target.value)} className={sel} data-testid="hfilter-type"><option value="">All Job Types</option>{jobTypes.map((c) => <option key={c} value={c}>{c}</option>)}</select>
            <select value={f.pay_type} onChange={(e) => set("pay_type", e.target.value)} className={sel} data-testid="hfilter-pay"><option value="">All Pay Types</option>{payTypes.map((c) => <option key={c} value={c}>{c}</option>)}</select>
            <select value={f.sort} onChange={(e) => set("sort", e.target.value)} className={sel} data-testid="hfilter-sort">
              <option value="newest">Newest</option><option value="deadline">Application deadline</option><option value="alpha">A to Z</option>
            </select>
            <label className="flex items-center gap-2 text-sm cursor-pointer"><input type="checkbox" checked={f.featured} onChange={(e) => set("featured", e.target.checked)} data-testid="hfilter-featured" /> Featured only</label>
          </div>
        </aside>
        <div className="lg:col-span-9">
          <p className="text-sm text-zinc-400 mb-5">{loading ? "Loading..." : `${data.total} open positions`}</p>
          <div className="grid sm:grid-cols-2 gap-5">{data.items.map((j) => <JobCard key={j.id} j={j} />)}</div>
          {!loading && data.items.length === 0 && <p className="text-zinc-500 py-20 text-center">No open jobs match your filters.</p>}
          <Pagination page={page} pages={data.pages} onPage={setPage} />
        </div>
      </div>
    </div>
  );
}
